import { useState } from 'react'
import { Input } from './input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs'
import { useOptionalMultilingualForm } from './multilingual-form-context'

interface MultilingualInputProps {
  value: { [key: string]: string }
  onChange: (value: { [key: string]: string }) => void
  placeholder?: string
  required?: boolean
  /** Renders the inputs read-only; the language tabs keep working so every
      stored translation stays visible. */
  disabled?: boolean
  id?: string
  className?: string
  'data-testid'?: string
}

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
] as const

export function MultilingualInput({
  value,
  onChange,
  placeholder = '',
  required = false,
  disabled = false,
  id,
  className,
  'data-testid': dataTestId
}: MultilingualInputProps) {
  const [localActiveTab, setLocalActiveTab] = useState('en')
  const context = useOptionalMultilingualForm()
  
  // Use context language if available, otherwise use local state
  const activeTab = context?.activeLanguage || localActiveTab
  const setActiveTab = context?.setActiveLanguage || setLocalActiveTab
  
  // Ensure value is always an object with default empty strings
  const normalizedValue = value || { en: '', fr: '' }

  const handleInputChange = (langCode: string, inputValue: string) => {
    const newValue = { ...normalizedValue, [langCode]: inputValue }
    onChange(newValue)
  }

  const hasValue = (langCode: string) => {
    return normalizedValue[langCode] && normalizedValue[langCode].trim().length > 0
  }

  const isValid = () => {
    if (!required) return true
    // For required fields, at least one language must have a value
    return Object.values(normalizedValue).some(val => val && val.trim().length > 0)
  }

  return (
    <div className={className}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          {LANGUAGES.map((lang) => (
            <TabsTrigger 
              key={lang.code} 
              value={lang.code}
              className="flex items-center gap-2 relative"
              data-testid={dataTestId ? `${dataTestId}-tab-${lang.code}` : undefined}
            >
              <span className="text-lg">{lang.flag}</span>
              <span>{lang.label}</span>
              {hasValue(lang.code) && (
                <div className="absolute -top-1 -right-1 h-2 w-2 bg-green-500 rounded-full" />
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        
        {LANGUAGES.map((lang) => (
          <TabsContent key={lang.code} value={lang.code} className="mt-4">
            <Input
              id={id ? `${id}-${lang.code}` : undefined}
              value={normalizedValue[lang.code] || ''}
              onChange={(e) => handleInputChange(lang.code, e.target.value)}
              placeholder={`${placeholder} (${lang.label})`}
              readOnly={disabled}
              className="w-full"
              data-testid={dataTestId ? `${dataTestId}-${lang.code}` : undefined}
            />
          </TabsContent>
        ))}
      </Tabs>
      
      {required && !isValid() && (
        <p className="text-sm text-destructive mt-2">
          At least one language must be filled in
        </p>
      )}
    </div>
  )
}